<template>
  <div class="metric-card">
    <div class="card-content">
      <div class="metric-header">
        <div class="metric-icon" :class="`icon-${iconType}`">
          <!-- Icon based on type -->
          <svg v-if="icon === 'users'" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
          </svg>
          <svg v-else-if="icon === 'revenue'" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clip-rule="evenodd" />
          </svg>
          <svg v-else-if="icon === 'courses'" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
          </svg>
          <svg v-else-if="icon === 'assessments'" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path fill-rule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clip-rule="evenodd" />
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="metric-change" :class="`change-${changeType}`">
          <span>{{ change }}</span>
          <svg v-if="trend === 'up'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clip-rule="evenodd" />
          </svg>
          <svg v-else-if="trend === 'down'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clip-rule="evenodd" />
          </svg>
        </div>
      </div>
      <div class="metric-body">
        <h3 class="metric-value">{{ value }}</h3>
        <p class="metric-title">{{ title }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'MetricCard',
  props: {
    title: {
      type: String,
      required: true
    },
    value: {
      type: [String, Number],
      required: true
    },
    change: {
      type: String,
      default: '0%'
    },
    changeType: {
      type: String,
      default: 'neutral',
      validator: value => ['positive', 'negative', 'neutral', 'info'].includes(value)
    },
    icon: {
      type: String,
      default: 'default'
    },
    trend: {
      type: String,
      default: 'stable',
      validator: value => ['up', 'down', 'stable'].includes(value)
    }
  },
  setup(props) {
    // Determine icon type based on icon name
    const iconType = computed(() => {
      const iconMapping = {
        users: 'primary',
        revenue: 'success',
        courses: 'warning',
        assessments: 'info',
        default: 'secondary'
      };
      
      return iconMapping[props.icon] || 'secondary';
    });

    return {
      iconType
    };
  }
};
</script>

<style scoped>
.metric-card {
  @apply bg-white rounded-lg shadow-sm overflow-hidden;
}

.card-content {
  @apply p-4 sm:p-6;
}

.metric-header {
  @apply flex justify-between items-start;
}

.metric-icon {
  @apply p-3 rounded-full;
}

.icon-primary {
  @apply bg-indigo-100 text-indigo-600;
}

.icon-success {
  @apply bg-green-100 text-green-600;
}

.icon-warning {
  @apply bg-yellow-100 text-yellow-600;
}

.icon-info {
  @apply bg-blue-100 text-blue-600;
}

.icon-secondary {
  @apply bg-gray-100 text-gray-600;
}

.metric-change {
  @apply flex items-center text-sm font-medium;
}

.change-positive {
  @apply text-green-600;
}

.change-negative {
  @apply text-red-600;
}

.change-neutral {
  @apply text-gray-600;
}

.change-info {
  @apply text-blue-600;
}

.metric-body {
  @apply mt-4;
}

.metric-value {
  @apply text-2xl font-bold text-gray-900;
}

.metric-title {
  @apply text-sm font-medium text-gray-500 mt-1;
}

@media (prefers-color-scheme: dark) {
  .metric-card {
    @apply bg-gray-800;
  }
  
  .metric-value {
    @apply text-gray-100;
  }
  
  .metric-title {
    @apply text-gray-400;
  }
  
  .icon-primary {
    @apply bg-indigo-900 bg-opacity-40 text-indigo-400;
  }
  
  .icon-success {
    @apply bg-green-900 bg-opacity-40 text-green-400;
  }
  
  .icon-warning {
    @apply bg-yellow-900 bg-opacity-40 text-yellow-400;
  }
  
  .icon-info {
    @apply bg-blue-900 bg-opacity-40 text-blue-400;
  }
  
  .icon-secondary {
    @apply bg-gray-700 text-gray-400;
  }
}
</style> 